import apiService from '@/services/apiService';
import printAgentService from '@/services/printAgentService';
import localPrintSettingsService from '@/services/localPrintSettingsService';

const RECOVERABLE_FISCAL_ERRORS = new Set([
  'cdc_no_disponible',
  'facturacion_electronica_result_not_final',
  'No se pudo obtener el ticket fiscal para impresión'
]);

async function fetchInvoiceTicketPayload({ saleId, movimientoId = null }) {
  const response = await apiService.get(`/api/printing/invoices/${saleId}/ticket`, {
    movimiento_id: movimientoId || undefined
  });
  return response?.data?.data || null;
}

async function fetchFiscalPrintJob({ saleId }) {
  const response = await apiService.get(`/api/printing/fiscal/invoices/${saleId}/job`);
  return response?.data?.data || null;
}

function decodeBase64ToBytes(base64) {
  const binary = window.atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }
  return bytes;
}

async function ensurePrintAgentAvailable() {
  try {
    await printAgentService.getHealth();
  } catch (error) {
    const nextError = new Error('El agente local de impresión no está disponible en http://127.0.0.1:9977.');
    nextError.code = 'print_agent_unavailable';
    nextError.cause = error;
    throw nextError;
  }
}

function normalizePrintError(error, settings = {}) {
  const apiError = error?.payload?.error || error?.code || '';

  // FE-RTK-002. Abortar el `fetch` NO cancela el trabajo del agente: puede
  // haber impreso igual. El mensaje tiene que admitir esa incertidumbre en vez
  // de afirmar un fallo que quizá no ocurrió — un mensaje que miente sobre lo
  // que pasó es peor que uno que reconoce que no sabe.
  if (apiError === 'print_agent_timeout' || error?.name === 'PrintAgentTimeoutError') {
    const nextError = new Error(
      'La impresión superó el tiempo de espera. El ticket puede haberse impreso igual — verificá antes de reintentar.'
    );
    nextError.code = 'print_agent_timeout';
    nextError.cause = error;
    throw nextError;
  }
  const detail = error?.payload?.detail || error?.message || '';
  const backend = settings?.backend || 'raw';
  const printerName = settings?.printerName || '';

  if (apiError === 'backend_down') {
    const suffix = printerName
      ? ` Verifica la impresora "${printerName}" y el backend "${backend}".`
      : ` Verifica la impresora configurada en el agente local y el backend "${backend}".`;
    const nextError = new Error(`El agente local respondió pero no pudo enviar el trabajo a la impresora.${suffix}`);
    nextError.code = 'print_backend_down';
    nextError.cause = error;
    nextError.detail = detail;
    throw nextError;
  }

  if (apiError === 'printer_not_found') {
    const nextError = new Error(
      printerName
        ? `La impresora local "${printerName}" no está disponible en el agente.`
        : 'No hay una impresora local válida configurada para imprimir.'
    );
    nextError.code = 'printer_not_found';
    nextError.cause = error;
    throw nextError;
  }

  if (apiError === 'duplicate_job') {
    return {
      ok: true,
      duplicate: true
    };
  }

  throw error;
}

async function tryPrintFiscalJob({ saleId, settings, fiscalJob: preloadedJob = null, idempotencyKey = null }) {
  // Si el cobro ya lo trajo, no se vuelve a pedir.
  const fiscalJob = preloadedJob || await fetchFiscalPrintJob({ saleId });
  if (!fiscalJob) {
    throw new Error('No se pudo obtener el ticket fiscal para impresión');
  }

  if (fiscalJob.encoding === 'base64' && fiscalJob.payloadBase64) {
    return printAgentService.printBytes({
      bytes: decodeBase64ToBytes(fiscalJob.payloadBase64),
      contentType: fiscalJob.contentType || 'application/octet-stream',
      printerName: settings.printerName,
      backend: settings.backend,
      copies: settings.copies,
      cut: settings.cut,
      apiKey: settings.apiKey,
      idempotencyKey: idempotencyKey || printAgentService.buildIdempotencyKey(`sale-${saleId}-fiscal`)
    });
  }

  if (fiscalJob.encoding === 'utf8') {
    return printAgentService.printBytes({
      bytes: new TextEncoder().encode(fiscalJob.payloadText || ''),
      contentType: fiscalJob.contentType || 'text/plain; charset=utf-8',
      printerName: settings.printerName,
      backend: settings.backend,
      copies: settings.copies,
      cut: settings.cut,
      apiKey: settings.apiKey,
      idempotencyKey: idempotencyKey || printAgentService.buildIdempotencyKey(`sale-${saleId}-fiscal`)
    });
  }

  throw new Error('Formato de ticket fiscal no soportado');
}

async function printInvoiceBySale({
  saleId,
  movimientoId = null,
  settings = null,
  // El cobro ya devuelve el ticket cuando el documento fiscal está listo
  // (SPEC_COBRO_PERFORMANCE_v0.1.md, Ampliación 1, RF-13). Recibirlo acá evita
  // el `GET /api/printing/fiscal/invoices/:id/job`, que es un viaje entero al
  // servidor — en producción, cruzando el Atlántico.
  printJob = null,
  // P2 RN-VI5: venta cobrada con ticket. No hay documento fiscal que pedir:
  // se va directo al ticket interno, que el API arma con su leyenda.
  sinFactura = false
}) {
  const resolvedSettings = localPrintSettingsService.sanitizeSettings(
    settings || localPrintSettingsService.getSettings()
  );

  await ensurePrintAgentAvailable();

  if (!sinFactura) {
    try {
      return await tryPrintFiscalJob({
        saleId,
        settings: resolvedSettings,
        fiscalJob: printJob
      });
    } catch (error) {
      // Un timeout corta acá y NO sigue al ticket interno. El trabajo fiscal
      // pudo haber salido: caer al camino de abajo imprimiría una segunda vez
      // algo que ya está en papel (FE-RTK-002).
      if (error?.code === 'print_agent_timeout' || error?.name === 'PrintAgentTimeoutError') {
        return normalizePrintError(error, resolvedSettings);
      }
      const backendError = error?.response?.data?.error || error?.message || '';
      if (backendError === 'backend_down' || backendError === 'printer_not_found' || backendError === 'duplicate_job') {
        return normalizePrintError(error, resolvedSettings);
      }
      if (backendError && !RECOVERABLE_FISCAL_ERRORS.has(backendError)) {
        console.warn('Fallo el ticket fiscal; se intentará el ticket interno', error);
      }
    }
  }

  const ticketData = await fetchInvoiceTicketPayload({ saleId, movimientoId });
  if (!ticketData?.printJob?.payload) {
    throw new Error('No se pudo obtener el contenido imprimible de la factura');
  }
  let printResult;
  try {
    printResult = await printAgentService.printJson({
      payload: ticketData.printJob.payload,
      printerName: resolvedSettings.printerName,
      backend: resolvedSettings.backend,
      copies: resolvedSettings.copies,
      cut: resolvedSettings.cut,
      apiKey: resolvedSettings.apiKey,
      idempotencyKey: printAgentService.buildIdempotencyKey(`sale-${saleId}`)
    });
  } catch (error) {
    return normalizePrintError(error, resolvedSettings);
  }

  return {
    ...printResult,
    mode: ticketData.mode || 'standard',
    contingency: ticketData.contingency || null
  };
}

// Imprime un documento fiscal que NO es la factura de la venta —hoy la nota de
// crédito (SPEC_ANULACION_FACTURAS_Y_NOTAS_CREDITO_v0.2.md RN-AN27)—, con su
// propia clave de idempotencia para no chocar con el ticket de la factura.
async function printFiscalDocument({ fiscalJob, idempotencySuffix, settings = null }) {
  const resolvedSettings = localPrintSettingsService.sanitizeSettings(
    settings || localPrintSettingsService.getSettings()
  );
  await ensurePrintAgentAvailable();
  try {
    return await tryPrintFiscalJob({
      saleId: fiscalJob?.saleId,
      settings: resolvedSettings,
      fiscalJob,
      idempotencyKey: printAgentService.buildIdempotencyKey(idempotencySuffix)
    });
  } catch (error) {
    return normalizePrintError(error, resolvedSettings);
  }
}

export default {
  fetchInvoiceTicketPayload,
  printInvoiceBySale,
  printFiscalDocument
};
